import { EnterOutlined, ProjectOutlined, PullRequestOutlined } from "@ant-design/icons";
import { Divider, InputNumber, Tooltip } from "antd";
import React, { ReactNode, useCallback } from 'react';
import AddTaskButton from "../../../../../../components/Basic/AddTaskButton";
import Icon, { ICON_NAME } from "../../../../../../components/Icon";
import Menu from "../../../../../../components/Menu";
import { patternFloat2, patternInteger, PROCESS_TYPE, SITE_TYPE } from "../../../../../../util/varibles/constants";
import { formatNumber } from "../../../../../../util/varibles/global";
import { ITaskTreatment, IUnit } from "../../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../../util/varibles/message";
import AddNote from "../../_components/AddNote";
import { IUnitWhenEdit, useEditOp } from "../../EditOperationContext";
import { initTask, moveUnitToNewRound } from "../constants";
import stylesContainer from "../style.module.scss";
import styles from "./style.module.scss";

const AddTaskIcon = () => <svg
    width='15'
    height='15'
    viewBox='0 0 15 15'
    fill='none'
    aria-hidden='true'
    className='block pointer-events-none'
>
    <path
        d='M7.5 2V13M2 7.5H13'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
    />
</svg>

const DensityPercent: React.FC<{ unit: IUnitWhenEdit }> = ({ unit }) => {
    const { density_percent } = unit;
    if (!density_percent)
        return null

    return <div className='flex items-center'>
        <Divider orientation='vertical' className='h-[24px]' />
        <div className='ml-[6px]'>
            Density: {Math.round(density_percent * 100)}%
        </div>
    </div>
}

interface IPropsLayout {
    menu: { key: string, label: ReactNode }[]
    dragHandleRef(element: Element | null): void
}

const Layout: React.FC<IPropsLayout> = ({ menu, dragHandleRef }) => {
    return <Menu trigger={['contextMenu']} items={menu}>
        <div ref={dragHandleRef} className={styles.layout} />
    </Menu>
}

interface IProps {
    unit: IUnitWhenEdit
    dragHandleRef(element: Element | null): void
    isDragging: boolean
}

const Title: React.FC<IProps> = (props) => {
    const { unit, dragHandleRef, isDragging } = props;
    const { id, total_weight = 0, fish_amount, avg_weight = '-', isLock, density_percent = 1, process_type } = unit;
    const [site, set] = useEditOp(state => state.site);
    const isInternal = site?.type === SITE_TYPE.INTERNAL;

    const save = useCallback((value: Partial<IUnit>) => {
        set(({ units }) => {
            const newUnit = { ...units[id], ...value };
            const avg_weight = newUnit.fish_amount > 0 ? Math.round(newUnit.total_weight / newUnit.fish_amount) : 0;
            return { units: { ...units, [id]: { ...newUnit, avg_weight } }, isCalculate: true }
        })
    }, [set, id]);

    const handleChangeFishAmount = useCallback((e: any) => {
        const text = e.target.value.trim();
        if (text.length > 0) {
            const reg = new RegExp(patternInteger);
            if (!reg.test(text)) {
                notify.error(NotifyCode.E9)();
                return;
            }
        }
        save({ fish_amount: +text, fish_amount_original: +text })
    }, [save])

    const handleChangeTotalWeight = useCallback((e: any) => {
        const text = e.target.value.trim();
        if (text.length > 0) {
            const reg = new RegExp(patternFloat2);
            if (!reg.test(text)) {
                notify.error(NotifyCode.E9)();
                return;
            }
        }
        save({ total_weight: text * 1000000 })
    }, [save])

    const handleDeleteUnit = useCallback(() => {
        set(({ units }) => ({
            units: { ...units, [id]: { ...units[id], isSorting: false, tasks: [] } },
            isCalculate: true
        }))
    }, [set, id])

    const handleAddTask = useCallback(() => {
        set(({ units }) => {
            const newTasks = Array.from(units[id].tasks as ITaskTreatment[] || []);
            const { isSorting } = units[id];
            const value = initTask(units[id])
            if (isSorting) {
                const last = newTasks.pop();
                if (last)
                    return { units: { ...units, [id]: { ...units[id], tasks: [...newTasks || [], value, last] } } }
            }
            return { units: { ...units, [id]: { ...units[id], tasks: [...newTasks || [], value] } } }
        })
    }, [set, id])

    const handleSaveNote = useCallback((note: string) => {
        set(({ units }) => ({ units: { ...units, [id]: { ...units[id], note } } }))
    }, [id, set]);

    const handleChangeDensityPercent = useCallback((value: number | null) => {
        set(({ units }) => {
            const { tank_number, ...args } = units[id];
            return {
                units: { ...units, [id]: { ...args, density_percent: value === null ? 1 : Math.round(value) / 100 } },
                isCalculate: true
            }
        })
    }, [id, set]);

    const handleChangeProcess = useCallback(() => {
        set(({ units }) => {
            const orderedIds = Object.keys(units).filter(unitId =>
                (units[unitId].tasks || []).length > 0
            );
            return {
                units: moveUnitToNewRound(units, orderedIds, id),
                isCalculate: true
            };
        })
    }, [id, set]);

    const menu = isLock ? [] : [
        {
            key: 'density',
            disabled: true,
            label: <div className='menu-line'>
                <ProjectOutlined />
                <div>
                    <span className='mr-2'>Density</span>
                    <InputNumber<number>
                        defaultValue={100}
                        min={0}
                        value={Math.round(density_percent * 100)}
                        formatter={(value) => `${value}%`}
                        parser={(value) => value?.replace('%', '') as unknown as number}
                        onChange={handleChangeDensityPercent}
                    />
                </div>
            </div>
        },
        ...process_type === PROCESS_TYPE.PARALLEL ? [{
            key: 'process_type',
            onClick: handleChangeProcess,
            label: <div className='menu-line'>
                <EnterOutlined />
                <span>Move unit to a new round as sequence</span>
            </div>
        }] : [],
        {
            key: 'delete',
            onClick: handleDeleteUnit,
            label: <div className='menu-line' data-danger='true'>
                <Icon icon={ICON_NAME.DELETE} danger />
                <span>Delete unit</span>
            </div>
        },
    ]

    return <div
        className={[styles.title, stylesContainer.title].join(' ')}
        data-dragging={isDragging || undefined}
    >
        <Layout
            menu={menu}
            dragHandleRef={dragHandleRef}
        />
        <div className={styles.left}>
            {process_type === PROCESS_TYPE.PARALLEL && <Tooltip title='Parallel processing'>
                <div className={styles['process-type-icon']}>
                    <PullRequestOutlined className="text-[11px]" />
                </div>
            </Tooltip>}
            <label>{[site?.name, unit.unit_id].join(', ')}</label>
            <DensityPercent unit={unit} />
            <Divider orientation='vertical' className='h-[26px]' />
            <div className='mt-[-1px]'>
                <AddNote
                    isTooltip
                    isContent
                    isBubble={false}
                    title={unit.unit_id}
                    value={unit.note || ''}
                    isLabel={false}
                    onChange={handleSaveNote}
                />
            </div>
        </div>
        <div className={styles.right}>
            {!isInternal ? <div className={styles.info}>
                <input
                    className={styles.input}
                    value={unit.fish_amount || ''}
                    onChange={handleChangeFishAmount}
                    placeholder='Amount'
                />
                <div />
                <div className={styles.weight}>
                    <input
                        className={styles.input}
                        value={Math.round(total_weight / 1000) / 1000}
                        onChange={handleChangeTotalWeight}
                        placeholder='Total weight'
                    />
                    <div className={styles.suffix}>t</div>
                </div>
            </div> : <div className={styles.info}>
                <div className='pr-3'>{formatNumber(fish_amount)}</div>
                <div className='pr-3'>{formatNumber(avg_weight)} gr</div>
                <div className='pr-3'>{formatNumber(Math.round(total_weight / 1000) / 1000)} t</div>
            </div>}
            {isLock
                ? <div className={stylesContainer.lock}>
                    <div className={stylesContainer.message}> - Has been implemented</div>
                    <div className={stylesContainer.icon}>
                        <Icon icon={ICON_NAME.LOCK} />
                    </div>
                </div>
                : <>
                    <AddTaskButton icon={<AddTaskIcon />} onClick={handleAddTask} />
                    <Menu items={menu} placement='bottomRight'>
                        <div className='three-dot z-10 relative' />
                    </Menu>
                </>}
        </div>
    </div>;
};

export default Title;
